import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

const sections = [
  {
    id: 'who-we-are',
    icon: 'ri-leaf-line',
    title: 'Who We Are',
    content: `Swasthya Retreat is an Ayurvedic wellness retreat located in Fuerteventura, Spain. We organise immersive healing experiences rooted in the ancient wisdom of Ayurveda. Our website is operated by Swasthya Retreat and can be reached at swasthya.retreats@gmail.com or by phone at +34 643 939 427.

We are committed to protecting your personal data and being transparent about how we use it in accordance with the EU General Data Protection Regulation (GDPR) and applicable Spanish data protection law.`,
  },
  {
    id: 'what-we-collect',
    icon: 'ri-database-2-line',
    title: 'What Data We Collect',
    content: `We collect personal data in the following situations:

**Retreat Bookings:** When you make a reservation, we collect your full name, email address, phone number, retreat dates, dietary requirements or health notes you provide, and payment information processed securely via Stripe. We never store your full card details on our servers.

**Newsletter Subscriptions:** When you subscribe to our newsletter, we collect your first name and email address.

**Contact & WhatsApp:** If you reach out to us via email or WhatsApp, we may retain the content of those communications and your contact details for up to 2 years.

**Website Usage (Cookies):** We collect anonymous analytics data about how visitors use our site — pages visited, time spent, browser type, and approximate location. This helps us improve your experience.`,
  },
  {
    id: 'how-we-use',
    icon: 'ri-shield-user-line',
    title: 'How We Use Your Data',
    content: `We use your data strictly for the following purposes:

• **To process your retreat booking** and communicate all relevant details before, during, and after your stay
• **To send you our newsletter** if you have subscribed — wellness tips, retreat news, and seasonal programmes
• **To process payments** securely through our payment partner Stripe
• **To improve our website** based on anonymous usage analytics
• **To comply with legal obligations**, including tax and accounting requirements
• **To respond to your enquiries** via email or WhatsApp

We will never sell, rent, or share your data with third parties for marketing purposes.`,
  },
  {
    id: 'legal-basis',
    icon: 'ri-scales-3-line',
    title: 'Legal Basis for Processing',
    content: `Under GDPR, we process your data on the following legal bases:

**Contractual necessity** — Processing your booking and payment is required to fulfil the contract between you and Swasthya Retreat.

**Legitimate interests** — We have a legitimate interest in maintaining records of correspondence, improving our website, and operating our business safely.

**Consent** — Newsletter subscriptions are based on your explicit consent. You may withdraw your consent at any time by clicking "unsubscribe" in any email or contacting us directly.

**Legal obligation** — We may be required to process certain data to comply with Spanish or EU legal requirements.`,
  },
  {
    id: 'cookies',
    icon: 'ri-cookie-line',
    title: 'Cookies Policy',
    content: `Our website uses cookies — small files stored on your device — for the following purposes:

**Essential Cookies:** Necessary for core site functionality such as remembering your consent choice. These cannot be disabled.

**Analytics Cookies:** We use anonymised analytics to understand how visitors interact with our site (pages viewed, session duration, device type). No personally identifiable data is collected.

**Functional Cookies:** Used to remember your language preference and other settings to improve your browsing experience.

When you first visit our website, you will see a cookie banner asking for your consent. You can accept or decline non-essential cookies at any time. Note that declining cookies may affect some functionality of the site.`,
  },
  {
    id: 'third-parties',
    icon: 'ri-links-line',
    title: 'Third-Party Services',
    content: `We work with the following trusted third-party services that may process your data on our behalf:

**Stripe (stripe.com):** Our payment processor. When you pay your retreat deposit, Stripe handles the transaction under their own Privacy Policy. Stripe is PCI-DSS certified and processes payments securely. We never see or store your full card number.

**WhatsApp (Meta):** If you choose to contact us via WhatsApp, your message is processed by Meta Platforms under their own terms of service.

**Google Fonts:** Our website loads fonts from Google's servers, which may collect your IP address. No other personal data is shared.

All third-party providers are required to handle your data in compliance with GDPR.`,
  },
  {
    id: 'your-rights',
    icon: 'ri-user-settings-line',
    title: 'Your Rights Under GDPR',
    content: `As a data subject under GDPR, you have the following rights:

**Right of Access:** You may request a copy of all personal data we hold about you.

**Right to Rectification:** You can ask us to correct any inaccurate or incomplete data.

**Right to Erasure ("Right to be Forgotten"):** You may request deletion of your data, subject to legal retention obligations.

**Right to Restriction:** You can ask us to limit how we process your data in certain circumstances.

**Right to Data Portability:** You may request your data in a structured, machine-readable format.

**Right to Object:** You can object to processing based on legitimate interests, including direct marketing.

**Right to Withdraw Consent:** If processing is based on consent (e.g. newsletter), you may withdraw it at any time without affecting prior processing.

To exercise any of these rights, contact us at swasthya.retreats@gmail.com. We will respond within 30 days.`,
  },
  {
    id: 'data-retention',
    icon: 'ri-time-line',
    title: 'How Long We Keep Your Data',
    content: `We retain your personal data only for as long as necessary:

• **Booking & payment records** — 7 years (required by Spanish tax law)
• **Newsletter subscriber data** — Until you unsubscribe or request deletion
• **General correspondence** — Up to 2 years from last contact
• **Cookie consent records** — 12 months, refreshed upon each new consent

After these periods, your data is securely deleted or anonymised.`,
  },
  {
    id: 'security',
    icon: 'ri-lock-password-line',
    title: 'Data Security',
    content: `We take the security of your data seriously. Our measures include:

• All data transmitted between your browser and our site is encrypted using SSL/TLS
• Payments are processed by Stripe, which holds PCI-DSS Level 1 certification — the highest standard in the payment industry
• Access to personal data is restricted to authorised personnel only
• We regularly review our security practices

Despite these precautions, no internet transmission is 100% secure. If you have concerns about the security of your data, please contact us.`,
  },
  {
    id: 'contact',
    icon: 'ri-mail-send-line',
    title: 'Contact & Complaints',
    content: `If you have any questions, concerns, or requests relating to this Privacy Policy or your personal data, please contact us:

**Email:** swasthya.retreats@gmail.com
**Phone:** +34 643 939 427
**WhatsApp:** +34 643 939 427

If you are not satisfied with our response, you have the right to lodge a complaint with the Spanish data protection authority (Agencia Española de Protección de Datos — AEPD) at www.aepd.es.

This Privacy Policy was last updated on 30 March 2026.`,
  },
];

const renderContent = (text: string) => {
  return text.split('\n').map((line, i) => {
    if (line.startsWith('**') && line.endsWith('**')) {
      return <p key={i} className="font-semibold text-stone-800 mt-4 mb-1">{line.replace(/\*\*/g, '')}</p>;
    }
    if (line.match(/^\*\*(.*?)\*\*/)) {
      const parts = line.split(/\*\*(.*?)\*\*/);
      return (
        <p key={i} className={`${line.startsWith('•') ? '' : ''} text-stone-600 leading-relaxed`}>
          {parts.map((part, j) =>
            j % 2 === 1 ? <strong key={j} className="text-stone-800">{part}</strong> : part
          )}
        </p>
      );
    }
    if (line.startsWith('•')) {
      return <li key={i} className="text-stone-600 leading-relaxed ml-2">{line.slice(1).trim()}</li>;
    }
    if (line.trim() === '') return <div key={i} className="h-2" />;
    return <p key={i} className="text-stone-600 leading-relaxed">{line}</p>;
  });
};

const PrivacyPolicyPage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="min-h-screen bg-stone-50">
      <Navigation />

      {/* Hero */}
      <div className="relative pt-32 pb-20 overflow-hidden" style={{ backgroundColor: '#2c2a25' }}>
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/50d48717d467b7d9e9f4ae9907102539.jpg')`,
          }}
        />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block text-xs uppercase tracking-widest font-semibold mb-4 px-4 py-1.5 rounded-full border" style={{ color: '#8aab8a', borderColor: '#8aab8a55' }}>
            Legal
          </span>
          <h1 className="font-serif text-5xl md:text-6xl font-bold text-white mb-4">Privacy Policy</h1>
          <p className="text-stone-300 text-lg max-w-2xl mx-auto leading-relaxed">
            We believe in full transparency about how we collect, use, and protect your personal data at Swasthya Retreat.
          </p>
          <p className="text-stone-500 text-sm mt-4">Last updated: 30 March 2026 &nbsp;·&nbsp; GDPR compliant</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Quick Nav */}
        <div className="bg-white rounded-2xl p-6 mb-14 border border-stone-100">
          <p className="text-xs uppercase tracking-wider font-semibold text-stone-400 mb-4">Contents</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {sections.map((s, i) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="flex items-center gap-2 text-sm text-stone-600 hover:text-stone-900 transition-colors cursor-pointer py-1 group"
              >
                <span className="text-xs text-stone-400 font-medium w-5">{String(i + 1).padStart(2, '0')}</span>
                <span className="group-hover:underline underline-offset-2">{s.title}</span>
              </a>
            ))}
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-12">
          {sections.map((section) => (
            <div key={section.id} id={section.id} className="scroll-mt-24">
              <div className="flex items-center gap-4 mb-5">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ backgroundColor: '#8aab8a22' }}>
                  <i className={`${section.icon} text-lg`} style={{ color: '#8aab8a' }}></i>
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-bold text-stone-900">{section.title}</h2>
              </div>
              <div className="pl-14 space-y-1">
                <ul className="list-none space-y-0.5">
                  {renderContent(section.content)}
                </ul>
              </div>
              <div className="mt-10 border-b border-stone-100" />
            </div>
          ))}
        </div>

        {/* Footer CTA */}
        <div className="mt-16 text-center py-12 rounded-2xl" style={{ backgroundColor: '#f6f8f5' }}>
          <i className="ri-shield-check-line text-3xl mb-3 block" style={{ color: '#8aab8a' }}></i>
          <p className="font-serif text-xl font-bold text-stone-800 mb-2">Questions about your data?</p>
          <p className="text-stone-500 text-sm mb-6">We\'re always happy to help. Reach out and we\'ll respond within 30 days.</p>
          <a
            href="mailto:swasthya.retreats@gmail.com"
            className="inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
            style={{ backgroundColor: '#8aab8a', color: '#1a1a1a' }}
          >
            <i className="ri-mail-line"></i>
            swasthya.retreats@gmail.com
          </a>
        </div>

        <div className="mt-10 text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-stone-400 hover:text-stone-700 transition-colors cursor-pointer">
            <i className="ri-arrow-left-line"></i>
            Back to homepage
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
